import * as R from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  getAllGeoTargetingConfigs,
  validateSharedRootUrls,
} from 'api/workspaces'
import { getOrgWorkspaces } from 'modules/workspace/selectors'
import { isEmptyOrNil } from 'utils'

const GLOBAL_WORKSPACE_ID = 'global'
const DEFAULT_WORKSPACE_ID = 'default'
interface GeoTargetingConfig {
  rootUrls: string[]
  geoCountryCode: string[]
}

export interface Workspace {
  id: string
  orgId: number
  name: string
  createdBy: number
  updatedBy: number
  status: string
  default: boolean
}

const workspaceHasGeo = (config: GeoTargetingConfig) =>
  !isEmptyOrNil(R.path(['geoCountryCode'], config))

/**
 * Hook that interfaces with workspaces regional targeting
 *
 * @returns { allGeoTargetingConfigs, orgHasWorkspaces, geoToWorkspaceMap, validateRootUrls, allWorkspaces }
 */
const useWorkspacesGeoTargeting = () => {
  const allWorkspaces = useSelector<string, Record<string, Workspace>>(
    getOrgWorkspaces
  )
  const [allGeoTargetingConfigs, setAllGeoTargetingConfigs] = useState({})
  const [hasFetchedGeoTargeting, setHasFetchedGeoTargeting] = useState(false)
  const [geoToWorkspaceMap, setGeoToWorkspaceMap] = useState<{
    [key: string]: string
  }>({})

  const getWorkspacesGeoTargetingConfigs = useCallback(() => {
    getAllGeoTargetingConfigs()
      .then((res) => {
        setAllGeoTargetingConfigs(res.data)
        setHasFetchedGeoTargeting(true)
      })
      .catch((err) => console.error(err))
  }, [])

  const validateRootUrls = useCallback(async (rootUrls) => {
    try {
      const { data } = await validateSharedRootUrls(rootUrls)
      return data
    } catch (err) {
      console.error(err)
    }
  }, [])

  // determine if an org has workspaces by filtering out global and default workspaces, if they still
  // have workspaces after filtering then the org has workspaces!
  const orgHasWorkspaces = useMemo(
    () =>
      !isEmptyOrNil(
        R.keys(allWorkspaces).filter(
          (workspaceId) =>
            !R.equals(workspaceId, GLOBAL_WORKSPACE_ID) &&
            !R.equals(workspaceId, DEFAULT_WORKSPACE_ID)
        )
      ),
    [allWorkspaces]
  )

  // initial geo targeting config fetch
  useEffect(() => {
    if (!hasFetchedGeoTargeting) {
      getWorkspacesGeoTargetingConfigs()
    }
  }, [getWorkspacesGeoTargetingConfigs, hasFetchedGeoTargeting])

  // build mapping { geoCountryCode : workspaceId }
  useEffect(() => {
    const geoToWorkspaceMap: { [key: string]: string } = {}
    const addGeosToMapping = (value: GeoTargetingConfig, key: string) => {
      value.geoCountryCode.forEach((countryCode: string) => {
        geoToWorkspaceMap[countryCode] = key
      })
    }
    if (!isEmptyOrNil(allGeoTargetingConfigs)) {
      const workspacesWithGeos: {} = R.filter(
        workspaceHasGeo,
        allGeoTargetingConfigs
      )
      R.forEachObjIndexed(addGeosToMapping, workspacesWithGeos)
      setGeoToWorkspaceMap(geoToWorkspaceMap)
    }
  }, [allGeoTargetingConfigs])

  return useMemo(
    () => ({
      allGeoTargetingConfigs,
      orgHasWorkspaces,
      geoToWorkspaceMap,
      validateRootUrls,
      allWorkspaces,
    }),
    [
      allGeoTargetingConfigs,
      orgHasWorkspaces,
      geoToWorkspaceMap,
      validateRootUrls,
      allWorkspaces,
    ]
  )
}

export default useWorkspacesGeoTargeting
